import { useState } from "react";
import ThemeToggle from "../../components/ThemeToggle";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, Mail, Key } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const LOGO_FULL_URL = "/images/logo-full.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
// Destinazioni ammesse dopo il login (niente open redirect).
const ALLOWED_NEXT = new Set(['/admin', '/visite']);

async function postJson(path, body, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

function errorText(data, fallback) {
  const d = data && data.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
}

export default function SuperAdminLogin() {
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [credentials, setCredentials] = useState({
    email: "",
    password: ""
  });
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [setupData, setSetupData] = useState(null);
  // Token pre-2FA (solo in memoria): vale per i passi 2FA, non per il pannello.
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = ALLOWED_NEXT.has(searchParams.get('next')) ? searchParams.get('next') : '/admin';

  const finishSuperAdminLogin = (data) => {
    localStorage.setItem('super_admin_token', data.access_token);
    localStorage.setItem('super_admin_user', JSON.stringify({ email: data.email, scope: 'super_admin' }));
    toast.success('Login super admin effettuato con successo');
    navigate(nextPath);
  };

  const sendLoginCode = async (token) => {
    const { res, data } = await postJson('/api/super-admin/2fa/generate-code', {}, token);
    if (!res.ok) throw new Error(errorText(data, 'Errore invio codice'));
    toast.info('Codice 2FA inviato via email');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Operatori CRM: login unificato. Il super admin viene rifiutato qui (403) e passa dal 2FA.
      const crm = await postJson('/api/admin/crm/login', credentials);
      const crmData = crm.res.ok ? crm.data : null;
      if (crmData && crmData.scope === 'crm_operatore') {
        const data = crmData;
        localStorage.setItem('super_admin_token', data.access_token);
        localStorage.setItem(
          'super_admin_user',
          JSON.stringify({
            email: data.email,
            scope: data.scope,
            modules: data.modules || null,
            permissions: data.permissions || null,
            profile: data.profile || null,
            name: data.name || '',
          })
        );
        const profileLabel =
          data.profile === 'contenuti_editor'
            ? 'Editor contenuti'
            : 'Operatore CRM';
        const modules = Array.isArray(data.modules) ? data.modules : [];
        if (modules.includes('commerciale-dashboard')) {
          sessionStorage.setItem('admin_open_module', 'commerciale-dashboard');
        }
        toast.success(`Accesso ${profileLabel}`);
        navigate('/admin');
        return;
      }

      const { res, data } = await postJson('/api/super-admin/login', credentials);
      if (!res.ok || !data.pre_auth_token) {
        throw new Error(errorText(data, 'Credenziali non valide'));
      }
      setPreAuthToken(data.pre_auth_token);
      setTwoFactorCode('');

      if (data.two_factor_enabled) {
        setSetupData(null);
        setStep('2fa');
        await sendLoginCode(data.pre_auth_token);
      } else {
        await setupTwoFactor(data.pre_auth_token);
      }
    } catch (error) {
      toast.error(error.message || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const setupTwoFactor = async (token) => {
    const { res, data } = await postJson('/api/super-admin/2fa/setup', {}, token);
    if (!res.ok) throw new Error(errorText(data, 'Errore configurazione 2FA'));
    setSetupData(data);
    setStep('2fa');
    toast.success('Codice 2FA inviato via email');
  };

  const handleTwoFactorVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!preAuthToken) throw new Error('Sessione scaduta. Effettua nuovamente il login.');
      const { res, data } = setupData
        ? await postJson('/api/super-admin/2fa/verify', { setup_id: setupData.setup_id, code: twoFactorCode }, preAuthToken)
        : await postJson('/api/super-admin/2fa/login-verify', { code: twoFactorCode }, preAuthToken);
      if (!res.ok || !data.access_token) throw new Error(errorText(data, 'Codice 2FA non valido'));
      if (setupData) toast.success('2FA configurato con successo!');
      finishSuperAdminLogin(data);
    } catch (error) {
      toast.error(error.message || 'Codice 2FA non valido');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      if (!preAuthToken) throw new Error('Sessione scaduta. Effettua nuovamente il login.');
      if (setupData) await setupTwoFactor(preAuthToken);
      else await sendLoginCode(preAuthToken);
    } catch (error) {
      toast.error(error.message || 'Errore invio codice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#D9E0EA] p-4 text-slate-900">
      <ThemeToggle className="fixed right-3 top-3 z-50" testId="theme-toggle-login" />
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src={LOGO_FULL_URL} alt="Affidya" className="h-12 w-auto" />
          <p className="mt-2 inline-flex h-[14px] items-center rounded-full bg-[#3F51B5] px-2.5 text-[11px] font-extrabold uppercase leading-none tracking-wide text-white">
            Amministrazione
          </p>
          <p className="mt-4 text-sm text-slate-500">
            {step === "credentials" ? "Accesso riservato amministratore sistema" : "Verifica in due fattori"}
          </p>
        </div>

        <div className="mb-6 flex justify-center">
          <div className="flex items-center space-x-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                step === "credentials" ? "bg-[#3F51B5] text-white" : "bg-emerald-600 text-white"
              }`}
            >
              {step === "credentials" ? "1" : "✓"}
            </div>
            <div className={`h-1 w-12 ${step === "2fa" ? "bg-[#3F51B5]" : "bg-slate-300"}`} />
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                step === "2fa" ? "bg-[#3F51B5] text-white" : "bg-slate-300 text-slate-600"
              }`}
            >
              2
            </div>
          </div>
        </div>

        <Card className="rounded-2xl border-slate-200/80 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
          <CardHeader className="pb-4">
            <CardTitle className="text-center text-slate-900">
              {step === "credentials" ? "Accedi al Pannello Admin" : "Inserisci Codice 2FA"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === "credentials" ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="email"
                      value={credentials.email}
                      onChange={(e) => setCredentials((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="Inserisci email"
                      className="border-slate-200 bg-slate-50 pl-10 text-slate-900 placeholder:text-slate-400"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={credentials.password}
                      onChange={(e) => setCredentials((prev) => ({ ...prev, password: e.target.value }))}
                      placeholder="Inserisci password"
                      className="border-slate-200 bg-slate-50 pl-10 pr-10 text-slate-900 placeholder:text-slate-400"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#3F51B5] text-white hover:bg-[#3648a3]"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Accesso in corso...
                    </div>
                  ) : (
                    "Accedi"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleTwoFactorVerify} className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Codice di Verifica</label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="border-slate-200 bg-slate-50 pl-10 text-center font-mono text-lg text-slate-900 placeholder:text-slate-400"
                      maxLength={6}
                      required
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Inserisci il codice a 6 cifre ricevuto via email
                  </p>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={resendCode}
                    className="text-sm text-[#3F51B5] hover:text-[#3648a3]"
                    disabled={loading}
                  >
                    Non hai ricevuto il codice? Invia di nuovo
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
                  disabled={loading || twoFactorCode.length !== 6}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Verifica in corso...
                    </div>
                  ) : (
                    "Verifica Codice"
                  )}
                </Button>

                {!setupData && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resendCode}
                    className="w-full border-[#3F51B5] text-[#3F51B5] hover:bg-[#3F51B5]/5"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-[#3F51B5]" />
                        Generazione in corso...
                      </div>
                    ) : (
                      "Genera Nuovo Codice"
                    )}
                  </Button>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPreAuthToken(null);
                    setStep("credentials");
                    setTwoFactorCode("");
                    setSetupData(null);
                  }}
                  className="w-full text-slate-500 hover:text-slate-800"
                >
                  ← Torna indietro
                </Button>
              </form>
            )}

            <div className="mt-6 rounded-xl bg-[#EEF0F3] p-3">
              <p className="text-center text-xs text-slate-500">
                <strong className="text-slate-700">Sicurezza avanzata:</strong> autenticazione a due
                fattori obbligatoria per il Super Admin. Le attività sono tracciate e loggate.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-slate-500 hover:text-slate-800"
          >
            ← Torna ad Affidya
          </Button>
        </div>
      </div>
    </div>
  );
}
